import { FolderOpen, Settings, BarChart, AlertTriangle, Activity, User } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '../../shared/ui';
import { useCurrentUser } from '../../shared/api';

interface HeaderProps {
  className?: string;
}

export function Header({ className = '' }: HeaderProps) {
  const { t } = useTranslation();
  const { data: currentUser } = useCurrentUser();

  return (
    <header
      className={`
        sticky top-0 z-50
        border-b border-border
        bg-background
        px-8 py-4
        ${className}
      `}
    >
      <div className="flex items-center justify-between gap-8">
        {/* Brand */}
        <div className="flex items-center gap-4">
          {/* Logo */}
          <img src="/android-chrome-192x192.png" alt="DevLint Logo" className="h-12 w-12" />

          {/* Brand Text */}
          <div className="flex flex-col">
            <h1 className="text-2xl font-bold text-text-primary font-mono tracking-tight">
              DevLint
            </h1>
            <p className="text-xs text-text-muted font-mono">{t('header.tagline')}</p>
          </div>
        </div>

        {/* Navigation & Actions */}
        <div className="flex flex-1 items-center justify-between gap-6">
          {/* Main Navigation */}
          <nav className="flex items-center gap-2">
            <Link to="/dashboard">
              <Button
                variant="ghost"
                size="sm"
                corners="asymmetric"
                icon={<BarChart className="h-4 w-4" />}
              >
                {t('nav.dashboard')}
              </Button>
            </Link>
            <Link to="/projects">
              <Button
                variant="ghost"
                size="sm"
                corners="asymmetric"
                icon={<FolderOpen className="h-4 w-4" />}
              >
                {t('nav.projects')}
              </Button>
            </Link>
            <Link to="/analysis">
              <Button
                variant="ghost"
                size="sm"
                corners="asymmetric"
                icon={<Activity className="h-4 w-4" />}
              >
                {t('nav.analysis')}
              </Button>
            </Link>
            <Link to="/issues">
              <Button
                variant="ghost"
                size="sm"
                corners="asymmetric"
                icon={<AlertTriangle className="h-4 w-4" />}
              >
                {t('nav.issues')}
              </Button>
            </Link>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            <Link to="/settings">
              <Button
                variant="ghost"
                size="icon"
                corners="asymmetric"
                icon={<Settings className="h-4 w-4" />}
              />
            </Link>

            {/* User Avatar */}
            <Link to="/settings" className="flex items-center">
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center overflow-hidden ring-2 ring-border hover:ring-primary transition-all duration-200">
                {currentUser?.avatar_url ? (
                  <img
                    src={currentUser.avatar_url}
                    alt={currentUser.full_name}
                    className="h-9 w-9 rounded-full object-cover"
                  />
                ) : (
                  <User className="h-5 w-5 text-text-inverse" />
                )}
              </div>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
